'use client'

// Import the crypto icons
import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { ConnectorManager } from '../../ConnectorManager'
import { useCoinGeckoPrices } from '../../../../hooks/use-coin-gecko-prices'
import Image from 'next/image'

interface AssetHolding {
  asset: string
  free: number
  locked: number
  total: number
  priceInUsdt: number
  usdValue: number
  // Enhanced with CoinGecko data
  logo?: string
  priceChange24h?: number
  pnl24h?: number
}

export default function Holdings() {
  const [assets, setAssets] = useState<AssetHolding[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const connectorManager = ConnectorManager.getInstance()

  // Use the CoinGecko hook to get price data
  const { prices: coinGeckoData, loading: coinGeckoLoading } =
    useCoinGeckoPrices(1, 250)

  useEffect(() => {
    const fetchAssets = async () => {
      setIsLoading(true)
      setError(null)

      try {
        // Get the Binance connector
        const binanceConnector = connectorManager.getConnector('binance')

        if (!binanceConnector) {
          throw new Error('Binance connector not found or not connected')
        }

        // Fetch user assets from the connector
        const assetsData = await binanceConnector.getUserAssets()

        // Wait for CoinGecko data to be loaded
        if (!coinGeckoLoading && coinGeckoData.length > 0) {
          // Enhance assets with CoinGecko data
          const enhancedAssets = assetsData.map(asset => {
            // Find matching coin in CoinGecko data
            const coinData = coinGeckoData.find(
              coin => coin.symbol.toLowerCase() === asset.asset.toLowerCase()
            )

            // Calculate 24h P&L if we have price change data
            let pnl24h = 0
            if (coinData?.price_change_percentage_24h) {
              // Calculate what the price was 24h ago
              const priceChange = coinData.price_change_percentage_24h / 100
              const price24hAgo = asset.priceInUsdt / (1 + priceChange)

              // Calculate P&L
              pnl24h = asset.total * (asset.priceInUsdt - price24hAgo)
            }

            // Use public path for crypto icons
            const cryptoIconPath = `/img/crypto-icons/${asset.asset.toLowerCase()}.svg`

            return {
              ...asset,
              logo: coinData?.image || cryptoIconPath,
              priceChange24h: coinData?.price_change_percentage_24h || 0,
              pnl24h
            }
          })

          setAssets(enhancedAssets)
        } else {
          setAssets(assetsData)
        }
      } catch (err: any) {
        console.error('Error fetching assets:', err)
        setError(
          err.message ||
            'Failed to fetch assets. Please ensure you are connected to Binance.'
        )
      } finally {
        setIsLoading(false)
      }
    }

    if (!coinGeckoLoading) {
      fetchAssets()
    }
  }, [coinGeckoLoading, coinGeckoData])

  if (isLoading || coinGeckoLoading)
    return <div className={styles.loadingState}>Loading holdings...</div>
  if (error) return <div className={styles.errorState}>{error}</div>
  if (assets.length === 0)
    return <div className={styles.emptyState}>No holdings found</div>

  // Calculate total portfolio value
  const totalValue = assets.reduce((sum, asset) => sum + asset.usdValue, 0)

  // Calculate total 24h P&L
  const total24hPnL = assets.reduce(
    (sum, asset) => sum + (asset.pnl24h || 0),
    0
  )

  return (
    <div className={styles.holdingsContainer}>
      <div className={styles.holdingsSummary}>
        <div className={styles.summaryCard}>
          <h3>Total Portfolio Value</h3>
          <p>
            $
            {totalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
        </div>
        <div className={styles.summaryCard}>
          <h3>24h P&L</h3>
          <p className={total24hPnL >= 0 ? styles.profit : styles.loss}>
            $
            {total24hPnL.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            <span className={styles.changePercentage}>
              (
              {totalValue > 0
                ? ((total24hPnL / totalValue) * 100).toFixed(2)
                : '0.00'}
              %)
            </span>
          </p>
        </div>
      </div>
      <div className={styles.holdingsTable}>
        <table>
          <thead>
            <tr>
              <th>Asset</th>
              <th>Total</th>
              <th>Available</th>
              <th>In Order</th>
              <th>Price (USDT)</th>
              <th>24h Change</th>
              <th>Value (USDT)</th>
              <th>24h P&L</th>
              <th>Allocation</th>
            </tr>
          </thead>
          <tbody>
            {assets.map(asset => {
              const allocationPercentage = (asset.usdValue / totalValue) * 100

              return (
                <tr key={asset.asset}>
                  <td className={styles.assetCell}>
                    {asset.logo && (
                      <Image
                        src={asset.logo}
                        alt={asset.asset}
                        width={24}
                        height={24}
                        className={styles.assetLogo}
                        onError={e => {
                          // If image fails to load, try fallback
                          const target = e.target as HTMLImageElement
                          target.onerror = null // Prevent infinite loop

                          // Try the PNG version if SVG fails
                          if (target.src.includes('.svg')) {
                            target.src = `/img/crypto-icons/${asset.asset.toLowerCase()}.png`
                          } else {
                            // If that also fails, use a generic fallback
                            target.src = `/img/crypto-icons/generic.svg`
                          }
                        }}
                        unoptimized={asset.logo.startsWith('http')} // Skip optimization for external URLs
                      />
                    )}
                    {asset.asset}
                  </td>
                  <td>{asset.total.toFixed(8)}</td>
                  <td>{asset.free.toFixed(8)}</td>
                  <td>{asset.locked.toFixed(8)}</td>
                  <td>
                    $
                    {asset.priceInUsdt.toFixed(
                      asset.priceInUsdt < 0.01 ? 8 : 2
                    )}
                  </td>
                  <td
                    className={
                      (asset.priceChange24h ?? 0) >= 0
                        ? styles.profit
                        : styles.loss
                    }
                  >
                    {asset.priceChange24h?.toFixed(2)}%
                  </td>
                  <td>
                    $
                    {asset.usdValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td
                    className={
                      (asset.pnl24h ?? 0) >= 0 ? styles.profit : styles.loss
                    }
                  >
                    $
                    {asset.pnl24h?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </td>
                  <td>{allocationPercentage.toFixed(2)}%</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
